import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import { useQuery } from '@tanstack/react-query';
import {
  useLocalSearchParams,
  useNavigation,
  useRouter,
  useSegments,
  type Href,
} from 'expo-router';
import { useSetAtom } from 'jotai';
import { CircleCheck } from 'lucide-react-native';
import { useCallback, useEffect, useMemo } from 'react';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { twJoin } from 'tailwind-merge';

import { headerItem } from '@/components/HeaderActions';
import { ProductSupportSuccess } from '@/components/MedicalOnDemand/ProductSupportSuccess';
import { SuccessScreen } from '@/components/SuccessScreen';
import { Header } from '@/components/header';
import { Badge, BadgeText } from '@/components/ui/badge';
import { Icon } from '@/components/ui/icon';
import { useBreakpointValue } from '@/components/ui/utils/use-break-point-value';
import { VStack } from '@/components/ui/vstack';
import { ANALYTICS_EVENTS } from '@/constants/analytics';
import { FEATURE_FLAGS } from '@/constants/featureFlags';
import { REFERRAL_COPY, INVITE_COPY_VERSION } from '@/constants/referrals';
import { type RiveAnimationName } from '@/constants/riveAnimations';
import { ROUTES } from '@/constants/routes';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { usePostSubmitSuccess } from '@/hooks/usePostSubmitSuccess';
import { getCancerTypesV2QueryOptions } from '@/services/getCancerTypesV2';
import { productSupportPrefillMessageAtom } from '@/services/globalState';
import { segmentClient } from '@/util/analytics';
import { getPostSubmitSuccessPrefillMessage } from '@/util/postSubmitSuccess';

export default function Success() {
  const local = useLocalSearchParams<{
    focusId?: string | string[];
    productSupport?: string | string[];
    productId?: string | string[];
    prefill?: string | string[];
    prefillMessage?: string | string[];
    testPrefill?: string | string[];
    testMessage?: string | string[];
  }>();

  const { navigate, replace } = useRouter();

  const navigation = useNavigation();

  const segments = useSegments();

  const setPrefillMessage = useSetAtom(productSupportPrefillMessageAtom);

  const isModProductSupportSuccessEnabled = useFeatureFlag(
    FEATURE_FLAGS.MOD_PRODUCT_SUPPORT_SUCCESS_SCREEN
  );

  const { bottom } = useSafeAreaInsets();

  const cancerFocusQuery = useQuery(getCancerTypesV2QueryOptions());

  const {
    success: postSubmitSuccess,
    focusId,
    isPrefillEnabled,
    prefillMessageParam,
  } = usePostSubmitSuccess(local, isModProductSupportSuccessEnabled);

  const cancerFocus = cancerFocusQuery.data?.cancerTypeLookup.get(Number(focusId));

  const isProductSupportSuccess = postSubmitSuccess.kind === 'productSupportSuccess';

  const prefillProductId =
    postSubmitSuccess.kind === 'productSupportSuccess'
      ? postSubmitSuccess.prefillProductId
      : undefined;

  const isCompactLayout = useBreakpointValue({ default: true, md: false }) ?? true;

  const isNativeCompact = !isWeb && isCompactLayout;

  const showBadgeInHeader = Boolean(isProductSupportSuccess && isNativeCompact);

  useEffect(() => {
    if (!isProductSupportSuccess || !isPrefillEnabled || !prefillProductId) {
      return;
    }

    const message = getPostSubmitSuccessPrefillMessage({
      prefillProductId,
      prefillMessageParam:
        typeof prefillMessageParam === 'string' ? prefillMessageParam : undefined,
    });

    if (!message) {
      return;
    }

    setPrefillMessage(message);
  }, [
    isProductSupportSuccess,
    isPrefillEnabled,
    prefillProductId,
    setPrefillMessage,
    prefillMessageParam,
  ]);

  const inquirySuccessOptions = useMemo(
    (): {
      riveAnimation: RiveAnimationName;
      heading: string;
      description: string;
      buttonText: string;
      buttonHref: Href;
    }[] => [
      {
        riveAnimation: 'GAIN_CLARITY_LIGHT_GREEN',
        heading: 'Get another perspective',
        description:
          'Your inquiry has been submitted! In the meantime, try submitting your case to another expert (or two!) \u2013 it doesn\u2019t hurt to confirm your approach!',
        buttonText: `Consult another ${cancerFocus?.shortLabel ?? 'oncology'} expert`,
        buttonHref: '/directory',
      },
      {
        riveAnimation: 'ACCESSING_NETWORK_LIGHT_GREEN',
        heading: `You\u2019re not the only one with ${cancerFocus?.label ?? 'oncology'} questions`,
        description:
          'Your inquiry has been submitted! While you wait for their reply, take a look at what your peers are asking.',
        buttonText: 'Visit the Inquiry Library',
        buttonHref: '/community',
      },
      {
        riveAnimation: 'ACCESSING_NETWORK_LIGHT_GREEN',
        heading: 'Grow the community',
        description: REFERRAL_COPY.referrer.inquirySuccessGrowCommunityDescription,
        buttonText: 'Invite a Peer',
        buttonHref: ROUTES.ACCOUNT_INVITES,
      },
      {
        riveAnimation: 'SHARING_INFORMATION_LIGHT_GREEN',
        heading: 'Consult with confidence',
        description:
          'Your inquiry has been submitted! Did you know Primum\u2019s specialists are compensated for answering your inquiries? Never hesitate to get the answers you need, guilt free.',
        buttonText: 'Consult an expert',
        buttonHref: '/directory',
      },
    ],
    [cancerFocus]
  );

  const randomIndex = useMemo(() => Math.floor(Math.random() * inquirySuccessOptions.length), []);

  const selected = inquirySuccessOptions[randomIndex];

  const handleCtaPress = useMemo(() => {
    if (selected.heading !== 'Grow the community') {
      return undefined;
    }

    return () => {
      segmentClient.track(ANALYTICS_EVENTS.INVITE_A_PEER_CLICKED, {
        source: 'inquiry_success_grow_community',
        invite_copy_version: INVITE_COPY_VERSION,
        referral_location: 'inquiry_success_grow_community',
        referral_role: 'referrer',
      });
    };
  }, [selected.heading]);

  /**
   * Takes this screen off its tab before switching to Inbox, so the tab does not reopen on it
   * the next time it is chosen. Only this screen goes: whatever the inquiry was written from,
   * such as a Research conversation or a profile, is still there when the reader comes back.
   *
   * The Inquiry tab's own composer is replaced by this screen rather than covered by it, so
   * there is nothing beneath to go back to. The tab root is put back in its place instead.
   */
  const goToInbox = useCallback(() => {
    const tabGroup = segments.find(
      (segment) => /^\(.+\)$/.test(segment) && segment !== '(secure)' && segment !== '(tabs)'
    );

    if ((navigation.getState()?.index ?? 0) > 0) {
      // eslint-disable-next-line no-restricted-syntax -- pops this tab's own stack before switching tabs, not a Back
      navigation.goBack();
    } else if (tabGroup) {
      replace(`/${tabGroup.slice(1, -1)}` as Href);
    }

    navigate('/inbox');
  }, [navigate, navigation, replace, segments]);

  const handleClose = useCallback(() => {
    setPrefillMessage('');

    goToInbox();
  }, [goToInbox, setPrefillMessage]);

  const handleSuccessPrimaryCta = useCallback(() => {
    segmentClient.track(ANALYTICS_EVENTS.MOD_PRODUCT_SUPPORT_SUCCESS_CTA_CLICKED, {
      source: 'product_support_success',
      action: 'edit_submit_to_product_support',
      productId: prefillProductId,
    });
  }, [prefillProductId]);

  const handleSuccessSkipToInbox = useCallback(() => {
    segmentClient.track(ANALYTICS_EVENTS.MOD_PRODUCT_SUPPORT_SUCCESS_INBOX_CLICKED, {
      source: 'product_support_success',
      action: 'continue_to_inbox',
      productId: prefillProductId,
    });

    setPrefillMessage('');

    goToInbox();
  }, [goToInbox, prefillProductId, setPrefillMessage]);

  const handleSuccessViewed = useCallback(() => {
    segmentClient.track(ANALYTICS_EVENTS.MOD_PRODUCT_SUPPORT_SUCCESS_VIEWED, {
      source: 'product_support_success',
      productId: prefillProductId,
    });
  }, [prefillProductId]);

  const handleSuccessContinueToProductSupport = useCallback(() => {
    if (postSubmitSuccess.kind !== 'productSupportSuccess') {
      return;
    }

    navigate(postSubmitSuccess.productSupportHref);
  }, [navigate, postSubmitSuccess]);

  const submittedBadge = (
    <Badge
      action="success"
      variant="outline"
      size={isNativeCompact ? 'md' : 'lg'}
      className={twJoin(
        'rounded-full border-success-300 bg-success-50',
        isNativeCompact ? 'px-1.5 py-0.5' : 'px-2.5 py-1'
      )}>
      <Icon
        as={CircleCheck}
        size={isNativeCompact ? 'lg' : 'xl'}
        className="mr-1 text-success-600"
      />

      <BadgeText className={twJoin('text-success-700', isNativeCompact ? '' : 'text-sm')}>
        Inquiry Submitted
      </BadgeText>
    </Badge>
  );

  return (
    <VStack
      className="flex-1 bg-primary-500"
      style={{ paddingBottom: isProductSupportSuccess ? 0 : bottom }}>
      <Header
        fill="brand"
        leftItems={[headerItem.close(handleClose)]}
        headerTitle={showBadgeInHeader ? () => submittedBadge : undefined}
      />

      {postSubmitSuccess.kind === 'productSupportSuccess' ? (
        <ProductSupportSuccess
          productName={postSubmitSuccess.productName}
          onContinueToProductSupport={handleSuccessContinueToProductSupport}
          onGoToInbox={handleSuccessSkipToInbox}
          onPrimaryCtaPress={handleSuccessPrimaryCta}
          onViewed={handleSuccessViewed}
        />
      ) : (
        <SuccessScreen
          heading={selected.heading}
          description={selected.description}
          riveAnimation={selected.riveAnimation}
          buttonText={selected.buttonText}
          buttonHref={selected.buttonHref}
          onButtonPress={handleCtaPress}
        />
      )}
    </VStack>
  );
}
